import CSSPlugin from 'gsap/CSSPlugin';
import TweenLite from 'gsap/TweenLite';
import React, {
  MutableRefObject,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import {
  millisecondsPerSeconds,
} from '../../Utils';
import { smallMediaQuery } from '../Grid';
import {
  defaultVerticalSlideAnimationDuration,
  primaryColor1,
} from '../Utils';
import {
  DEFAULT_Z_INDEX,
} from './usePromotableZIndexStaticListOfElements';
import usePropsChangeRateLimiter, {
  PropsChangeHandlerInput,
} from './usePropsChangeRateLimiter';
import {
  staticCircleOuterRadius,
  zIndices,
} from './Utils';

// Need to do this so that `CSSPlugin` is not dropped by the minifier:
/* tslint:disable-next-line:no-unused-expression */
CSSPlugin;

//#region Styling
const staticCircleBorderWidth = 1; // in px
const dynamicCircleBorderWidth = 2; // in px
const dynamicCircleRadius = staticCircleOuterRadius - staticCircleBorderWidth - dynamicCircleBorderWidth / 2;

const tooltipBackgroundColor = '#5065a8';
const Root = styled.div`
  width: ${staticCircleOuterRadius * 2}px;
  height: ${staticCircleOuterRadius * 2}px;
  position: relative;
  cursor: pointer;
  z-index: ${zIndices.normal};

  --icon-color-1: #7585bd;
  --icon-color-2: ${primaryColor1};
  --icon-background-color: transparent;

  &:hover {
    --icon-color-1: white;
    --icon-color-2: white;
    --icon-background-color: ${tooltipBackgroundColor};
  }

  @media ${smallMediaQuery} {
    width: ${staticCircleOuterRadius * 1.6}px;
    height: ${staticCircleOuterRadius * 1.6}px;
  }
`;
const SVG = styled.svg`
  position: absolute;
  top: 0;
  left: 0;

  @media ${smallMediaQuery} {
    display: none;
  }
`;
// Need this factor so that the start and end points of the circular arc are not the same.
// If they are, no arc is drawn:
const separationFactor = 0.99;
const path =
/* tslint:disable-next-line:max-line-length */
  `M ${staticCircleOuterRadius} ${staticCircleBorderWidth + dynamicCircleBorderWidth / 2} A ${dynamicCircleRadius},${dynamicCircleRadius} 0 1 1 ${(staticCircleOuterRadius) * separationFactor} ${staticCircleBorderWidth + dynamicCircleBorderWidth / 2}`;

const TooltipContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: ${tooltipBackgroundColor};
  padding: 1.25rem;
  color: white;
`;
const TooltipTitle = styled.div`
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  white-space: nowrap;
  margin-bottom: 0.5rem;
`;
const TooltipContent = styled.ul`
  margin-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.4);
`;
const TooltipItemContainer = styled.li`
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 0.375rem;
  white-space: nowrap;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 300;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:first-child {
    margin-top: 0.5rem;
  }

  &::after {
    display: inline-block;
    width: 0.75rem;
    content: url(${require('./svgs-loaded-with-file-loader/arrow-right.svg')});
    margin-left: 0.375rem;
  }
`;
const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 50%;
  border: 1px solid ${primaryColor1};
  background-color: var(--icon-background-color);
`;
export const svgIconInternalStyling = css`
  .cls-1 {
    stroke-width: 1.1;
    stroke: var(--icon-color-1);
    fill: none;
  }

  .cls-2 {
    stroke-width: 0.7;
    stroke: var(--icon-color-1);
    fill: none;
  }

  .cls-3 {
    stroke-width: 1.5;
    stroke: var(--icon-color-1);
    fill: none;
  }

  .cls-4 {
    stroke-width: 2;
    stroke: var(--icon-color-1);
    fill: none;
  }

  .cls-5 {
    stroke-width: 1;
    stroke: var(--icon-color-2);
    fill: var(--icon-color-2);
  }
`;
const ImageInnerContainer = styled.div`
  position: relative;
  width: 60%;
  height: 0;
  padding-top: 100%;

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
    width: 100%;
    height: 100%;
    ${svgIconInternalStyling}
  }
`;
//#endregion

// Allow CSS custom properties
declare module 'csstype' {
  interface Properties {
    '--icon-color-1'?: string;
    '--icon-color-2'?: string;
  }
}

const getStrokeDashOffsetForPercentage =
  (percentage: number, pathLength: number): number => ((1 - percentage / 100) * pathLength);

interface IPropsChangeHandlerExtraInputs {
  pathElRef: MutableRefObject<SVGPathElement | null>;
  pathLengthRef: MutableRefObject<number | undefined>;
  transitionDuration: number;
}
const performPropsChange =
  (input: PropsChangeHandlerInput<IPropsChangeHandlerExtraInputs, number>) => {

  const {
    prevValue, nextValue, done,
    extraInputs: {
      pathElRef: {current: pathEl},
      pathLengthRef,
      transitionDuration: innerTransitionDuration,
    },
  } = input;

  if (pathEl !== null) {
    const normalizedPrevValue = (prevValue === undefined) ? 0 : prevValue;

    let pathLength: number;
    if (pathLengthRef.current === undefined) {
      pathLength = pathEl.getTotalLength();
      pathLengthRef.current = pathLength;
      TweenLite.set(pathEl, {
        css: {
          'stroke-dasharray': `${pathLength} ${pathLength}`,
          'stroke-dashoffset': getStrokeDashOffsetForPercentage(normalizedPrevValue, pathLength),
        },
        immediateRender: true,
      });
    } else {
      pathLength = pathLengthRef.current;
    }

    if (normalizedPrevValue === nextValue) {
      done();
    } else {
      TweenLite.fromTo(
        pathEl,
        innerTransitionDuration / millisecondsPerSeconds,
        {
          css: {'stroke-dashoffset': getStrokeDashOffsetForPercentage(normalizedPrevValue, pathLength)},
        }, {
          css: {'stroke-dashoffset': getStrokeDashOffsetForPercentage(nextValue, pathLength)},
          onComplete: done,
        },
      );
    }
  } else {
    done();
  }
};

const processPercentage = (input: number) => {
  const rounded = Math.round(input);
  return (rounded === 100) ? 99.9 : rounded;
};

interface ITooltipItem {
  label: string;
  slideIndex: number;
}

interface ITooltipItemProps extends ITooltipItem {
  onItemClick: (slideIndex: number) => void;
}

const TooltipItem = (props: ITooltipItemProps) => {
  const {slideIndex, onItemClick} = props;
  const elRef = useRef<HTMLLIElement | null>(null);

  const onClick = () => {
    onItemClick(slideIndex);
  };
  return (
    <TooltipItemContainer onClick={onClick} ref={elRef}>
      {props.label}
    </TooltipItemContainer>
  );
};

interface IProps {
  // `progress` is a number from 0 to 100
  progress: number;
  title: string;
  svgFileName: string;
  tooltipItems: ITooltipItem[];
  onTooltipItemClick: (slideIndex: number) => void;

  zIndex: typeof DEFAULT_Z_INDEX | number;
  promoteZIndex: () => void;
  restoreDefaultZIndex: () => void;
}
export default (props: IProps) => {
  const {
    title, tooltipItems, onTooltipItemClick, svgFileName,
    zIndex, promoteZIndex, restoreDefaultZIndex,
  } = props;
  const percentage = processPercentage(props.progress);

  const [isTooltipShown, setIsTooltipShown] = useState<boolean>(false);
  const showTooltip = () => {
    setIsTooltipShown(true);
    promoteZIndex();
  };

  const hideTooltip = () => {
    setIsTooltipShown(false);
    restoreDefaultZIndex();
  };

  const elRef = useRef<HTMLDivElement | null>(null);
  const pathElRef = useRef<SVGPathElement | null>(null);
  const pathLengthRef = useRef<number | undefined>(undefined);

  const extraInputs: IPropsChangeHandlerExtraInputs = {
    pathElRef,
    pathLengthRef,
    transitionDuration: defaultVerticalSlideAnimationDuration,
  };

  usePropsChangeRateLimiter({
    value: percentage,
    getExtraInputToPropsChangeHandler: () => extraInputs,
    performPropsChange,
  });

  let tooltip: React.ReactElement<any> | null;
  if (isTooltipShown === true) {
    const items = tooltipItems.map(({slideIndex, label}) => {
      const onItemClick = (index: number) => {
        hideTooltip();
        onTooltipItemClick(index);
      };

      return (
        <TooltipItem
          slideIndex={slideIndex}
          label={label}
          onItemClick={onItemClick}
          key={`item-${slideIndex}`}
        />
      );
    });

    tooltip = (
      <TooltipContainer>
        <TooltipTitle>{title}</TooltipTitle>
        <TooltipContent>{items}</TooltipContent>
      </TooltipContainer>
    );
  } else {
    tooltip = null;
  }

  const style: React.CSSProperties = {
    zIndex: (zIndex === DEFAULT_Z_INDEX) ? undefined : zIndex,
  };

  return (
    <Root ref={elRef} style={style} onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
      <SVG width={staticCircleOuterRadius * 2} height={staticCircleOuterRadius * 2}>
        <path
          d={path}
          stroke={percentage === 0 ? 'rgba(0,0,0,0)' : '#e2e2e2'}
          strokeWidth={dynamicCircleBorderWidth}
          fill='none'
        />
      </SVG>
      <SVG width={staticCircleOuterRadius * 2} height={staticCircleOuterRadius * 2}>
        <path
          d={path}
          stroke={primaryColor1}
          strokeWidth={dynamicCircleBorderWidth}
          fill='none'
          ref={pathElRef}
        />
      </SVG>
      <ImageContainer >
        <ImageInnerContainer dangerouslySetInnerHTML={{__html: require('./icons/' + svgFileName)}}/>
      </ImageContainer>
      {tooltip}
    </Root>
  );
};
